import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import {
  validateVietnamesePhone
} from "../utils/phoneValidation";
import { userSession, orderApi } from "../services";

function CheckinPage({ onNavigate }) {
  const [formData, setFormData] = useState({
    phone: "",
    name: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showUncheckoutWarning, setShowUncheckoutWarning] = useState(false);
  const [pendingOrder, setPendingOrder] = useState(null);

  // Load existing session data on component mount
  useEffect(() => {
    const session = userSession.getSession();
    console.log('Existing session:', session); // Debug log
    if (session && session.phone && session.name) {
      setFormData({
        phone: session.phone,
        name: session.name,
      });
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};

    // Validate phone number
    if (!formData.phone.trim()) {
      newErrors.phone = "Vui lòng nhập số điện thoại";
    } else if (!validateVietnamesePhone(formData.phone)) {
      newErrors.phone = "Số điện thoại không đúng định dạng";
    }

    // Validate name
    if (!formData.name.trim()) {
      newErrors.name = "Vui lòng nhập họ và tên";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Họ và tên phải có ít nhất 2 ký tự";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const checkForUncheckoutOrders = async (phoneNumber) => {
    try {
      const response = await orderApi.checkOrderStatus(phoneNumber);    
      if (response.ok && response.payload) {
          setPendingOrder({order_id: response.payload })
          return true; // Has uncheckout order
      }
      return false; // No uncheckout orders
    } catch (error) {
      console.error('Error checking for uncheckout orders:', error);
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Format phone number
      const formattedPhone = formData.phone.replace(/[^\d+]/g, '');

      // Check for uncheckout orders first
      const hasUncheckoutOrder = await checkForUncheckoutOrders(formattedPhone);
      
      if (hasUncheckoutOrder) {
        // Don't proceed with checkin, warning popup will be shown
        setShowUncheckoutWarning(true);
        setLoading(false);
        return;
      }

      // Save user session data
      const sessionData = {
        phone: formattedPhone,
        name: formData.name.trim(),
        checkinTime: new Date().toISOString(),
      };

      userSession.saveSession(sessionData);

      // Navigate to order page
      onNavigate("order");
    } catch (error) {
      console.error("Error during checkin:", error);
      setErrors({ general: "Có lỗi xảy ra, vui lòng thử lại" });
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrderDetail = () => {
    if (pendingOrder) {
      onNavigate(`/order-detail/${pendingOrder.order_id || pendingOrder.id}`);
    }
    setShowUncheckoutWarning(false);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#fff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        paddingTop: 40,
      }}
    >
      <div
        style={{
          background: "#fff",
          width: 320,
          minHeight: 600,
          borderRadius: 8,
          padding: 32,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Header
          onCartClick={() => {
            onNavigate(-1);
          }}
          onMenuClick={() => {
            onNavigate("/");
          }}
        />

        <div style={{ width: "100%", marginBottom: 20, marginTop: 100 }}>
          <label style={{ fontWeight: 400, fontSize: 16 }}>Số điện thoại</label>
          <input
            type="tel"
            name="phone"
            placeholder="Nhập số điện thoại"
            value={formData.phone}
            onChange={handleInputChange}
            style={{
              ...inputStyle,
              borderColor: errors.phone ? "#ff4444" : "#5b6754",
            }}
          />
          {errors.phone && (
            <div style={{ color: "#ff4444", fontSize: 12, marginTop: 4 }}>
              {errors.phone}
            </div>
          )}
        </div>

        <div style={{ width: "100%", marginBottom: 20 }}>
          <label style={{ fontWeight: 400, fontSize: 16 }}>Họ và Tên</label>
          <input
            type="text"
            name="name"
            placeholder="Nhập Họ và Tên"
            value={formData.name}
            onChange={handleInputChange}
            style={{
              ...inputStyle,
              borderColor: errors.name ? "#ff4444" : "#5b6754",
            }}
          />
          {errors.name && (
            <div style={{ color: "#ff4444", fontSize: 12, marginTop: 4 }}>
              {errors.name}
            </div>
          )}
        </div>

        {errors.general && (
          <div
            style={{
              color: "#ff4444",
              fontSize: 12,
              marginBottom: 12,
              textAlign: "center",
            }}
          >
            {errors.general}
          </div>
        )}

        <button
          style={{
            ...btnStyle,
            opacity: loading ? 0.7 : 1,
            cursor: loading ? "not-allowed" : "pointer",
          }}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "Đang xử lý..." : "Check-in"}
        </button>
      </div>

      {/* Uncheckout Warning Modal */}
      {showUncheckoutWarning && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            width: 320,
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{ 
              fontSize: 18, 
              fontWeight: 600, 
              color: '#ff4444',
              marginBottom: 16
            }}>
              ⚠️ Cảnh báo
            </div>
            <p style={{ 
              marginBottom: 16, 
              fontSize: 14, 
              color: '#666',
              lineHeight: 1.5
            }}>
              Bạn có đơn hàng chưa check-out. Vui lòng hoàn thành đơn hàng trước.
            </p>
            
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={handleViewOrderDetail}
                style={{
                  flex: 1,
                  height: 40,
                  background: '#5b6754',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 20,
                  fontSize: 14,
                  cursor: 'pointer',
                  fontFamily: 'NeueEinstellung',
                  fontWeight: 500
                }}
              >
                Xem chi tiết
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  width: "100%",
  height: 44,
  border: "1.5px solid #5b6754",
  borderRadius: 22,
  padding: "0 44px 0 16px",
  fontSize: 16,
  marginTop: 4,
  outline: "none",
  boxSizing: "border-box",
};

const btnStyle = {
  width: 160,
  height: 44,
  background: "#5b6754",
  color: "#fff",
  border: "none",
  borderRadius: 24,
  fontSize: 18,
  margin: "12px 0",
  cursor: "pointer",
  fontFamily: "NeueEinstellung",
  fontWeight: 300,
  float: "right",
};

export default CheckinPage;
